import { CellWrapperProps } from "@/components/elements/Advanced/Table/types";
import { Elements } from "@/components/elements/Elements";
import { Note, Notebook } from "@/lib/types";
import { WeatherParameters } from "@/components/elements/Weather/types";
import { TodoType } from "@/services/todolist";
import { DBSchema, IDBPDatabase, IDBPTransaction } from "idb";
import type { Schema as V0005Schema } from "./v0005";

export interface Schema extends DBSchema {
  appState:
    | {
        key: `elements.visibility.${string}`;
        value: boolean | null;
      }
    | {
        key: "activeProfileId";
        value: string | null;
      }
    | {
        key: "ai.provider";
        value: string | null;
      };
  elements: {
    key: string;
    value: {
      id: string;
      name: keyof typeof Elements;
      parentId: string | null;
      childrenIds: string[];
      profileId: string | null;
      config: string;
    };
    indexes: {
      id: string;
      profileId: string;
    };
  };
  stickyNotes: {
    key: string;
    value: {
      elementId: string;
      content: string;
    };
  };
  weatherConfig: {
    key: string;
    value: {
      elementId: string;
    } & WeatherParameters;
  };
  todos: {
    key: string;
    value: TodoType;
    indexes: {
      id: string;
    };
  };
  notebooks: {
    key: string;
    value: Notebook;
    indexes: {
      id: string;
    };
  };
  requests: {
    key: string;
    value: {
      id: string;
      url: string;
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      isPaginated: boolean;
      pagination:
        | {
            type: "basic";
            countField: string;
          }
        | {
            type: "cursor";
            cursorType: "index" | "date";
          }
        | null;
      headers: {
        enabled: boolean;
        key: string;
        value: string;
      }[];
      queries: {
        enabled: boolean;
        pageField: boolean;
        perPageField: boolean;
        key: string;
        value: string;
      }[];
      auth:
        | {
            type: "none";
          }
        | {
            type: "basic";
            username: string;
            password: string;
          }
        | {
            type: "bearer";
            token: string;
            prefix: string;
          };
      body: string;
    };
    indexes: {
      id: string;
      url: string;
      method: string;
    };
  };
  tableConfigurations: {
    key: string;
    value: {
      id: string;
      requestId: string;
      arrayField: string;
      countField: string;
      title: string;
      columns: {
        enabled: boolean;
        fieldName: string;
        columnName: string;
        columnType:
          | "string"
          | "number"
          | "boolean"
          | "date"
          | "array"
          | "object"
          | "unknown";
        columnDisplay: string;
        columnDisplayProps: Record<string, unknown>;
        columnStyle: CellWrapperProps;
      }[];
    };
    indexes: {
      id: string;
      requestId: string;
    };
  };
}

export const migration = async (
  _: IDBPDatabase<V0005Schema>,
  tx: IDBPTransaction<V0005Schema>,
) => {
  const notebooks = await tx.objectStore("notebooks").getAll();
  const notes = await tx.objectStore("notes").getAll();

  const uncategorizedNotebookId = crypto.randomUUID();
  const uncategorizedNotebook: Notebook = {
    id: uncategorizedNotebookId,
    title: "",
    icon: "Folder",
    vaultId: null,
    vault: null,
    notes: notes
      .filter(note => !note.notebookId)
      .map(
        note =>
          ({
            ...note,
            notebookId: uncategorizedNotebookId,
            notebook: undefined,
          }) as unknown as Note,
      ),
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const newNotebooks: Notebook[] = [
    uncategorizedNotebook,
    ...notebooks.map(
      notebook =>
        ({
          id: notebook.id,
          title: notebook.title,
          icon: notebook.icon,
          vaultId: "",
          vault: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          notes: notes
            .filter(note => note.notebookId === notebook.id)
            .map(
              note =>
                ({
                  id: note.id,
                  title: note.title,
                  icon: note.icon,
                  notebookId: notebook.id,
                  notebook: undefined as unknown as Notebook,
                  userId: "",
                  user: null,
                  content: note.content,
                  tags: note.tags,
                  vaultId: "",
                  vault: null,
                  createdAt: note.createdAt || new Date(),
                  updatedAt: note.updatedAt || new Date(),
                }) as Note,
            ),
        }) as Notebook,
    ),
  ];

  await (tx.objectStore("notebooks") as any).clear();
  await (tx.objectStore("notes") as any).clear();

  for (const notebook of newNotebooks) {
    await (tx.objectStore("notebooks") as any).put(notebook);
  }
};
